// Solar Dashboard Component - Manages photovoltaic financial data
import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  LinearProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  useTheme,
  Tooltip as MuiTooltip,
  IconButton,
  TextField,
  InputAdornment,
  Slider,
  Divider,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';
import { 
  InfoOutlined, 
  TrendingUp, 
  AccountBalanceWallet, 
  WbSunny,
  ArrowForward,
  Assessment,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
  AreaChart,
  Area
} from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { Expense } from '../types/Expense';
import { designTokens } from '../theme/designTokens';
import StatCard from './StatCard';
import { format, parseISO, startOfMonth, subMonths, isBefore } from 'date-fns';

interface SolarDashboardProps {
  expenses: Expense[];
}

const CAPEX_TOTAL = 7299;
const OWN_CAPITAL = 2299;
const CREDIT_TOTAL = 5142.51; // Reference principal
const MONTHLY_PAYMENT = 63.07;
const BASELINE_START_DATE = '2025-05-01'; // Baseline calculation starts in May 2025
const INSTALLATION_DATE = '2026-05-05'; // Panels will be installed on May 5, 2026
const ACCOUNTING_START_DATE = '2026-06-01'; // Start accounting savings from June 2026

export const SolarPanelsDashboard: React.FC<SolarDashboardProps> = ({ expenses }) => {
  const intl = useIntl();
  const theme = useTheme();
  
  // State for Simulator and Tabs
  const [extraPayment, setExtraPayment] = useState<number>(0);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // 1. Calculate Baseline (LUZ average before panels)
  const solarData = useMemo(() => {
    const luzExpenses = expenses.filter(e => e.category.toLowerCase().includes('luz'));
    
    // Expenses before panels (but after BASELINE_START_DATE)
    const beforePanels = luzExpenses.filter(e => {
      const date = parseISO(e.date);
      return !isBefore(date, parseISO(BASELINE_START_DATE)) && isBefore(date, parseISO(INSTALLATION_DATE));
    });
    
    // Group by month to get monthly average
    const monthlyGroups: { [key: string]: number } = {};
    beforePanels.forEach(e => {
      const month = format(parseISO(e.date), 'yyyy-MM');
      monthlyGroups[month] = (monthlyGroups[month] || 0) + e.amount;
    });
    
    const baselineMonths = Object.keys(monthlyGroups).length;
    const baselineAverage = baselineMonths > 0 
      ? Object.values(monthlyGroups).reduce((a, b) => a + b, 0) / baselineMonths 
      : 120; // Fallback value if no history

    // 2. Monthly analysis after panels
    const afterPanels = luzExpenses.filter(e => !isBefore(parseISO(e.date), parseISO(ACCOUNTING_START_DATE)));
    
    // Group after-panel expenses by month
    const postMonthlyGroups: { [key: string]: number } = {};
    afterPanels.forEach(e => {
      const month = format(parseISO(e.date), 'yyyy-MM');
      postMonthlyGroups[month] = (postMonthlyGroups[month] || 0) + e.amount;
    });

    // Generate month list for the table (last 12 months post-installation or all)
    const sortedMonths = Object.keys(postMonthlyGroups).sort().reverse();
    
    let accumulatedSavings = 0;
    const tableData = sortedMonths.map(month => {
      const billAmount = postMonthlyGroups[month];
      const currentTotalCost = billAmount + MONTHLY_PAYMENT;
      const savings = baselineAverage - currentTotalCost;
      
      // Wife pays: half of current bill + half of theoretical payment
      const wifeTransfer = (billAmount / 2) + (MONTHLY_PAYMENT / 2);
      
      // Profit for me: the savings generated
      const myProfit = savings;
      
      return {
        month,
        billAmount,
        currentTotalCost,
        savings,
        wifeTransfer,
        myProfit
      };
    });

    // Bank Debt Calculation
    const creditStartDate = parseISO('2026-05-05');
    const today = new Date();
    let monthsElapsed = 0;
    if (!isBefore(today, creditStartDate)) {
      monthsElapsed = (today.getFullYear() - creditStartDate.getFullYear()) * 12 + (today.getMonth() - creditStartDate.getMonth());
      if (today.getDate() < creditStartDate.getDate()) monthsElapsed--;
      monthsElapsed = Math.max(0, monthsElapsed);
    }
    
    // Simple linear approximation for remaining debt (or could be more precise with PMT formula if needed)
    // Here we use the fact that it's 96 months.
    const debtRemaining = Math.max(0, CREDIT_TOTAL * (1 - (monthsElapsed / 96)));
    const debtPaid = CREDIT_TOTAL - debtRemaining;
    const debtProgress = (debtPaid / CREDIT_TOTAL) * 100;
    
    // Total recovered so far
    const totalRecovered = tableData.reduce((sum, d) => sum + (d.savings > 0 ? d.savings : 0), 0);
    
    // YOC Calculation
    const averageMonthlySavings = tableData.length > 0 
      ? tableData.reduce((sum, d) => sum + d.savings, 0) / tableData.length 
      : 0;
    const estimatedAnnualSavings = averageMonthlySavings * 12;
    const yieldOnCost = (estimatedAnnualSavings / CAPEX_TOTAL) * 100;

    // Break-even Calculation
    let breakEvenDate = null;
    let monthsToBreakEven = null;
    if (averageMonthlySavings > 0) {
      const remainingTotal = Math.max(0, CAPEX_TOTAL - totalRecovered);
      monthsToBreakEven = Math.ceil(remainingTotal / averageMonthlySavings);
      breakEvenDate = new Date();
      breakEvenDate.setMonth(breakEvenDate.getMonth() + monthsToBreakEven);
    }
    
    // YoY Comparison Logic
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const yoyData = monthNames.map((name, index) => {
      // Historical average for this specific month (e.g. all Mays before solar)
      const beforeInMonth = beforePanels.filter(e => parseISO(e.date).getMonth() === index);
      const histAverage = beforeInMonth.length > 0 
        ? beforeInMonth.reduce((sum, e) => sum + e.amount, 0) / beforeInMonth.length 
        : 0;
        
      // Current value for this specific month after solar
      const afterInMonth = afterPanels.filter(e => parseISO(e.date).getMonth() === index);
      const currentVal = afterInMonth.length > 0 
        ? afterInMonth.reduce((sum, e) => sum + e.amount, 0) / afterInMonth.length 
        : null; // Use null to not show bar if no data
        
      return {
        month: name,
        before: histAverage,
        after: currentVal
      };
    });
    
    // Cumulative Cash Flow Logic
    let currentBalance = -CAPEX_TOTAL;
    const cumulativeFlowData = tableData.slice().reverse().map(d => {
      currentBalance += d.savings;
      return {
        month: format(parseISO(`${d.month}-01`), 'MMM yy'),
        balance: currentBalance,
        zero: 0
      };
    });

    // Detailed Amortization Schedule (composition)
    let ownCapitalLeft = OWN_CAPITAL;
    let bankDebtLeft = CREDIT_TOTAL;
    const amortizationSchedule = tableData.slice().reverse().map(d => {
      const reductionOwn = Math.min(ownCapitalLeft, Math.max(0, d.savings));
      ownCapitalLeft -= reductionOwn;
      
      const reductionBank = Math.min(bankDebtLeft, MONTHLY_PAYMENT);
      bankDebtLeft -= reductionBank;
      
      return {
        month: d.month,
        savings: d.savings,
        reductionOwn,
        reductionBank,
        ownRemaining: ownCapitalLeft,
        bankRemaining: bankDebtLeft
      };
    });

    // Interest savings estimation for simulator
    // 4% TAN = 0.333% per month
    const monthlyRate = 0.04 / 12;
    const monthsSaved = extraPayment > 0 
      ? Math.round(extraPayment / MONTHLY_PAYMENT) // Simplified
      : 0;
    const interestSaved = extraPayment * 0.04 * (monthsSaved / 12); // Very rough estimate
    
    // Environmental Impact
    // Estimating generation based on savings (approx 0.20€/kWh)
    const totalKwhSaved = totalRecovered / 0.20;
    const co2SavedKg = totalKwhSaved * 0.22; // 0.22kg CO2 per kWh in PT
    const treesEquivalent = co2SavedKg / 20; // 20kg CO2 per tree per year
    
    // History Matrix Logic
    const luzHistoryByYear: { 
      [year: string]: { 
        months: { [month: string]: number }, 
        total: number 
      } 
    } = {};

    luzExpenses.forEach(e => {
      const date = parseISO(e.date);
      const year = format(date, 'yyyy');
      const month = format(date, 'MM'); 
      
      if (!luzHistoryByYear[year]) {
        luzHistoryByYear[year] = { months: {}, total: 0 };
      }
      
      luzHistoryByYear[year].months[month] = (luzHistoryByYear[year].months[month] || 0) + e.amount;
      luzHistoryByYear[year].total += e.amount;
    });

    const allYearsAsc = Object.keys(luzHistoryByYear).sort();
    
    const comparativeTableData = Array.from({ length: 12 }, (_, i) => {
      const monthIndex = (i + 1).toString().padStart(2, '0');
      const rowData: any = { month: monthIndex, values: {} };
      
      allYearsAsc.forEach(year => {
        rowData.values[year] = luzHistoryByYear[year].months[monthIndex] || 0;
      });
      
      return rowData;
    });

    const comparativeChartData = Array.from({ length: 12 }, (_, i) => {
      const monthIndex = (i + 1).toString().padStart(2, '0');
      const rowData: any = { month: monthIndex };
      
      allYearsAsc.forEach(year => {
        const val = luzHistoryByYear[year].months[monthIndex];
        rowData[year] = val || null;
      });
      
      return rowData;
    });

    return {
      baselineAverage,
      tableData,
      totalRecovered,
      yieldOnCost,
      estimatedAnnualSavings,
      debtRemaining,
      debtProgress,
      monthsElapsed,
      breakEvenDate,
      monthsToBreakEven,
      yoyData,
      cumulativeFlowData,
      amortizationSchedule,
      interestSaved,
      monthsSaved,
      co2SavedKg,
      treesEquivalent,
      comparativeTableData,
      comparativeChartData,
      allYearsAsc,
      luzHistoryByYear,
      sortedMonths: sortedMonths.slice().reverse() // For charts
    };
  }, [expenses]);

  const roiProgress = (solarData.totalRecovered / OWN_CAPITAL) * 100;
  const remainingOwnCapital = Math.max(0, OWN_CAPITAL - solarData.totalRecovered);

  // Delta Chart Data
  const deltaChartData = solarData.tableData.slice(0, 6).reverse().map(d => ({
    name: format(parseISO(`${d.month}-01`), 'MMM yy'),
    oldBill: solarData.baselineAverage,
    newBillTotal: d.currentTotalCost,
    savings: d.savings
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '1600px', margin: '0 auto', width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: 4, 
        p: 4, 
        borderRadius: designTokens.borderRadius.xl, 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: 'white',
        boxShadow: designTokens.shadows.xl,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3
      }}>
        <WbSunny sx={{ 
          position: 'absolute', 
          right: -30, 
          top: -30, 
          fontSize: 240, 
          opacity: 0.05,
          transform: 'rotate(15deg)'
        }} />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" fontWeight="900" sx={{ mb: 1, letterSpacing: '-0.02em' }}>
            <FormattedMessage id="solar.title" />
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, opacity: 0.8 }}>
            <Typography variant="body1">
              <FormattedMessage id="solar.installation" />: <strong>5 <FormattedMessage id="months.may" /> 2026</strong>
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="body1">
              <FormattedMessage id="solar.capexTotal" />: <strong>€7.299</strong>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ p: 2, borderRadius: 3, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', minWidth: 100 }}>
            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>Yield on Cost</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#fbbf24' }}>{solarData.yieldOnCost.toFixed(1)}%</Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: 3, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', minWidth: 100 }}>
            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}><FormattedMessage id="solar.co2Saved" /></Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#34d399' }}>{Math.round(solarData.co2SavedKg)}kg</Typography>
          </Box>
          <Box sx={{ p: 2, borderRadius: 3, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center', minWidth: 100 }}>
            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}><FormattedMessage id="solar.breakEven" /></Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#10b981' }}>{solarData.breakEvenDate ? format(solarData.breakEvenDate, 'MMM yyyy') : '---'}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          textColor="primary" 
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={intl.formatMessage({ id: 'solar.overview' })} sx={{ fontWeight: 'bold', px: 3 }} />
          <Tab label={intl.formatMessage({ id: 'solar.performance' })} sx={{ fontWeight: 'bold', px: 3 }} />
          <Tab label={intl.formatMessage({ id: 'solar.historyTab' })} sx={{ fontWeight: 'bold', px: 3 }} />
          <Tab label={intl.formatMessage({ id: 'solar.amortizationTab' })} sx={{ fontWeight: 'bold', px: 3 }} />
          <Tab label={intl.formatMessage({ id: 'solar.environmentalTab' })} sx={{ fontWeight: 'bold', px: 3 }} />
        </Tabs>
      </Box>

      {/* Tab 1: Overview */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp color="primary" /> <FormattedMessage id="solar.monthlyEvolution" />
                </Typography>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={deltaChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(val) => `€${val}`} />
                      <Tooltip 
                        formatter={(value: number) => `€${value.toFixed(2)}`}
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: designTokens.shadows.lg }}
                      />
                      <Legend />
                      <Bar name="Nova Fatura + Crédito" dataKey="newBillTotal" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Line name="Média Base (Sem Painéis)" type="monotone" dataKey="oldBill" stroke="#ef4444" strokeWidth={3} dot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StatCard
                  title="Poupança Média Mensal"
                  value={`€${(solarData.estimatedAnnualSavings / 12).toFixed(2)}`}
                  icon={<WbSunny />}
                  gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                />
              </Grid>
              <Grid item xs={12}>
                <StatCard
                  title="Retorno Recuperado"
                  value={`€${solarData.totalRecovered.toFixed(2)}`}
                  icon={<AccountBalanceWallet />}
                  gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                />
              </Grid>
              <Grid item xs={12}>
                <StatCard
                  title="Custos Iniciais (CAPEX)"
                  value="€7.299,00"
                  icon={<InfoOutlined />}
                  gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}><FormattedMessage id="solar.settlementHistory" /></Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Fatura LUZ</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Prestação</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>Transferência Simone</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>Poupança Gerada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {solarData.tableData.map((row) => (
                        <TableRow key={row.month} hover>
                          <TableCell>{format(parseISO(`${row.month}-01`), 'MMMM yyyy')}</TableCell>
                          <TableCell align="right">€{row.billAmount.toFixed(2)}</TableCell>
                          <TableCell align="right">€{MONTHLY_PAYMENT.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>€{row.wifeTransfer.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: row.savings > 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                            €{row.savings.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Performance */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}><FormattedMessage id="solar.cumulativeFlow" /></Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}><FormattedMessage id="solar.profitReverts" /></Typography>
                <Box sx={{ height: 450 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={solarData.cumulativeFlowData}>
                      <defs>
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.5} />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(val) => `€${val}`} />
                      <Tooltip formatter={(value: number) => `€${value.toFixed(2)}`} />
                      <ReferenceLine y={0} stroke="#64748b" strokeWidth={2} strokeDasharray="5 5" />
                      <Area type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorBalance)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 4 }}><FormattedMessage id="solar.yoyComparison" /></Typography>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={solarData.yoyData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(val) => `€${val}`} />
                      <Tooltip formatter={(value: number) => value ? `€${value.toFixed(2)}` : '---'} />
                      <Legend />
                      <Bar name="Sem Painéis (Média)" dataKey="before" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      <Bar name="Com Painéis" dataKey="after" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 3: History LUZ */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                  Evolução Anual
                </Typography>
                <Box sx={{ height: 350, mb: 4 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={solarData.comparativeChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={(val) => intl.formatMessage({ id: `month.${parseInt(val) - 1}` }).substring(0, 3)} 
                      />
                      <YAxis tickFormatter={(val) => `€${val}`} />
                      <Tooltip 
                        formatter={(value: number, name: string) => [`€${value.toFixed(2)}`, name]}
                        labelFormatter={(label) => intl.formatMessage({ id: `month.${parseInt(label) - 1}` })}
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: designTokens.shadows.lg }}
                      />
                      <Legend />
                      {solarData.allYearsAsc.map((year, idx) => {
                        const YEAR_COLORS = ['#94a3b8', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                        return (
                          <Bar 
                            key={year} 
                            name={year} 
                            dataKey={year} 
                            fill={YEAR_COLORS[idx % YEAR_COLORS.length]} 
                            radius={[4, 4, 0, 0]} 
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                
                <Divider sx={{ mb: 4 }} />
                
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                  <FormattedMessage id="solar.historyTab" />
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="solar.historyTableMonth" /></TableCell>
                        {solarData.allYearsAsc.map((year, idx) => (
                          <React.Fragment key={year}>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{year}</TableCell>
                            {idx > 0 && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Var %</TableCell>}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {solarData.comparativeTableData.map((row) => (
                        <TableRow key={row.month} hover>
                          <TableCell>
                            {intl.formatMessage({ id: `month.${parseInt(row.month) - 1}` })}
                          </TableCell>
                          {solarData.allYearsAsc.map((year, idx) => {
                            const val = row.values[year];
                            const prevYear = idx > 0 ? solarData.allYearsAsc[idx - 1] : null;
                            const prevVal = prevYear ? row.values[prevYear] : 0;
                            const yoy = prevVal > 0 ? ((val - prevVal) / prevVal) * 100 : null;

                            return (
                              <React.Fragment key={year}>
                                <TableCell align="right">
                                  {val > 0 ? `€${val.toFixed(2)}` : '---'}
                                </TableCell>
                                {idx > 0 && (
                                  <TableCell align="right" sx={{ 
                                    color: yoy !== null ? (yoy > 0 ? 'error.main' : 'success.main') : 'text.secondary',
                                    fontWeight: 'bold'
                                  }}>
                                    {yoy !== null ? `${yoy > 0 ? '+' : ''}${yoy.toFixed(1)}%` : '---'}
                                  </TableCell>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      ))}
                      
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}><FormattedMessage id="solar.historyTableTotal" /></TableCell>
                        {solarData.allYearsAsc.map((year, idx) => {
                          const val = solarData.luzHistoryByYear[year].total;
                          const prevYear = idx > 0 ? solarData.allYearsAsc[idx - 1] : null;
                          const prevVal = prevYear ? solarData.luzHistoryByYear[prevYear].total : 0;
                          const yoy = prevVal > 0 ? ((val - prevVal) / prevVal) * 100 : null;

                          return (
                            <React.Fragment key={`total-${year}`}>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                €{val.toFixed(2)}
                              </TableCell>
                              {idx > 0 && (
                                <TableCell align="right" sx={{ 
                                  color: yoy !== null ? (yoy > 0 ? 'error.main' : 'success.main') : 'text.secondary',
                                  fontWeight: 'bold'
                                }}>
                                  {yoy !== null ? `${yoy > 0 ? '+' : ''}${yoy.toFixed(1)}%` : '---'}
                                </TableCell>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 4: Amortization */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 4 }}><FormattedMessage id="solar.activeMetrics" /></Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold"><FormattedMessage id="solar.amortizationCounter" /></Typography>
                    <Typography variant="subtitle2">{Math.round(roiProgress)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(roiProgress, 100)} 
                    sx={{ height: 12, borderRadius: 6, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: '#f59e0b' } }} 
                  />
                  <Typography variant="caption" color="textSecondary">Restam €{remainingOwnCapital.toFixed(2)} dos €2.299 iniciais.</Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold"><FormattedMessage id="solar.bankDebt" /></Typography>
                    <Typography variant="subtitle2">{Math.round(solarData.debtProgress)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={solarData.debtProgress} 
                    sx={{ height: 12, borderRadius: 6, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: '#3b82f6' } }} 
                  />
                  <Typography variant="caption" color="textSecondary">Restam €{solarData.debtRemaining.toFixed(2)} de crédito bancário.</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md, border: '2px solid #e2e8f0' }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom><FormattedMessage id="solar.extraAmortization" /></Typography>
                <Box sx={{ mt: 3, mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>Quanto pretende amortizar hoje?</Typography>
                  <Slider value={extraPayment} onChange={(_, val) => setExtraPayment(val as number)} min={0} max={5000} step={100} valueLabelDisplay="auto" />
                  <TextField fullWidth size="small" type="number" value={extraPayment} onChange={(e) => setExtraPayment(Number(e.target.value))} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} />
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, display: 'flex', justifyContent: 'space-around' }}>
                  <Box textAlign="center">
                    <Typography variant="caption" color="textSecondary" display="block">Meses Poupados</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">{solarData.monthsSaved}</Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" color="textSecondary" display="block">Juros Poupados (Est.)</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">€{solarData.interestSaved.toFixed(2)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, boxShadow: designTokens.shadows.md }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}><FormattedMessage id="solar.combinedAmortization" /></Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Mês</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Abate Capital</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Abate Crédito</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Capital Próprio</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Banco</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {solarData.amortizationSchedule.map((row) => (
                        <TableRow key={row.month}>
                          <TableCell>{format(parseISO(`${row.month}-01`), 'MMM yyyy')}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>+€{row.reductionOwn.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: 'primary.main' }}>+€{row.reductionBank.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: row.ownRemaining === 0 ? 'bold' : 'normal' }}>
                            €{row.ownRemaining.toFixed(2)} {row.ownRemaining === 0 && '✅'}
                          </TableCell>
                          <TableCell align="right">€{row.bankRemaining.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 5: Environmental Impact */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, textAlign: 'center', p: 4, background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: '#10b981', margin: '0 auto mb 2', fontSize: 40 }}>🌍</Avatar>
              <Typography variant="h5" fontWeight="bold" color="#064e3b"><FormattedMessage id="solar.carbonFootprint" /></Typography>
              <Typography variant="h2" fontWeight="900" color="#10b981" sx={{ my: 2 }}>{Math.round(solarData.co2SavedKg)}kg</Typography>
              <Typography variant="body1" color="#065f46">kg <FormattedMessage id="solar.carbonFootprint" />.</Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, textAlign: 'center', p: 4, background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: '#16a34a', margin: '0 auto mb 2', fontSize: 40 }}>🌳</Avatar>
              <Typography variant="h5" fontWeight="bold" color="#064e3b"><FormattedMessage id="solar.treesEquivalent" /></Typography>
              <Typography variant="h2" fontWeight="900" color="#16a34a" sx={{ my: 2 }}>{solarData.treesEquivalent.toFixed(1)}</Typography>
              <Typography variant="body1" color="#065f46"><FormattedMessage id="solar.treesPlanted" /></Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: designTokens.borderRadius.lg, textAlign: 'center', p: 4, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: '#3b82f6', margin: '0 auto mb 2', fontSize: 40 }}>🚗</Avatar>
              <Typography variant="h5" fontWeight="bold" color="#1e3a8a"><FormattedMessage id="solar.greenMobility" /></Typography>
              <Typography variant="h2" fontWeight="900" color="#3b82f6" sx={{ my: 2 }}>{Math.round(solarData.co2SavedKg * 5)}km</Typography>
              <Typography variant="body1" color="#1e40af"><FormattedMessage id="solar.combustionAvoided" /></Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ borderRadius: designTokens.borderRadius.lg, p: 3, bgcolor: '#f8fafc' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom><FormattedMessage id="solar.envWhy" /></Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="solar.envDescription" />
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};


